/**
 * MarkdownPreview — Theme-aware rendered markdown display.
 *
 * Composes:
 * - DocumentThemes: markdownToHtml() + themeToCSS() for rendering
 * - theemr-lib (ThemeSettings shape): Same property structure
 * - convertr-lib (DocxThemeAdapter): Same presets used for DOCX export
 *
 * Renders raw markdown as styled HTML using the selected document theme.
 * The theme CSS is injected via a <style> element scoped to the preview
 * container. Themes mirror the DOCX export presets so preview ≈ print.
 */

import { useMemo, useRef, useEffect } from 'react';
import { getDocumentTheme, markdownToHtml, themeToCSS } from './DocumentThemes.js';

interface MarkdownPreviewProps {
  content: string;
  themeId: string;
  /** If true, fill available height (for split view) */
  fillHeight?: boolean;
}

export function MarkdownPreview({ content, themeId, fillHeight }: MarkdownPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Get theme and generate CSS
  const theme = useMemo(() => getDocumentTheme(themeId), [themeId]);
  const css = useMemo(() => themeToCSS(theme.settings), [theme]);
  const html = useMemo(() => markdownToHtml(content), [content]);

  // Generate a stable style ID to avoid duplicate <style> elements
  const styleId = `md-preview-theme-${themeId}`;

  // Inject/update scoped CSS
  useEffect(() => {
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
    return () => {
      // Clean up on unmount
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [css, styleId]);

  if (!content.trim()) {
    return (
      <div
        style={{
          flex: fillHeight ? 1 : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.4,
          fontSize: 13,
          padding: 32,
          background: theme.settings.colors.background,
          color: theme.settings.colors.textSecondary,
        }}
      >
        Start writing to see a preview
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        flex: fillHeight ? 1 : undefined,
        overflow: 'auto',
        height: fillHeight ? '100%' : undefined,
      }}
    >
      <div
        className="md-preview"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
